import { zodResolver } from '@hookform/resolvers/zod';
import { type AdminSelf, isApiError } from '@saay/api-client';
import { formatCount } from '@saay/formatting';
import {
  applyFieldErrors,
  Button,
  CommandFeedback,
  Dialog,
  EmptyState,
  FieldErrorList,
  Icon,
  NativeSelect,
  SheetSection,
  Skeleton,
  StatusChip,
  TextField,
  useToast,
} from '../kit.js';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useOutletContext } from 'react-router';
import { z } from 'zod';

import type { MerchantCategory, MerchantCategoryCreate } from '../api.js';
import { type CommandOutcome, useCommand } from '@saay/web-app';
import { useSubmitBlocked } from '../components/form.js';
import { QueryState, usePageTitle } from '../components/guard.js';
import { onSheetOpenChange, useCloseSheet } from '../components/route-sheet.js';
import { canPerform } from '../permissions.js';
import { setupKeys, useCategories } from '../queries.js';
import type { MerchantsOutletContext } from './merchants-list.js';

const categorySchema = z.object({
  nameFa: z.string().trim().min(1, 'نام دسته‌بندی الزامی است.').max(100, 'حداکثر ۱۰۰ کاراکتر.'),
  parentCategoryId: z.string(),
});
type CategoryInput = z.input<typeof categorySchema>;
type CategoryValues = z.output<typeof categorySchema>;
const FIELDS = ['nameFa', 'parentCategoryId'];

function CreateCategoryForm({
  categories,
  initialParentId = '',
  onCreated,
}: {
  readonly categories: readonly MerchantCategory[];
  readonly initialParentId?: string;
  readonly onCreated?: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const offline = useSubmitBlocked();
  const [unmapped, setUnmapped] = useState<ReturnType<typeof applyFieldErrors>>([]);
  const form = useForm<CategoryInput, unknown, CategoryValues>({
    defaultValues: { nameFa: '', parentCategoryId: initialParentId },
    resolver: zodResolver(categorySchema),
  });
  const command = useCommand<MerchantCategoryCreate, MerchantCategory>(
    'merchant-category.create',
    (client, body, intent) => client.post('/admin/merchant-categories', { body, intent }),
    {
      onSuccess: async (result) => {
        await queryClient.invalidateQueries({ queryKey: setupKeys.categories });
        toast.success({ confirmedBy: result, title: `دسته‌بندی «${result.data.pathFa}» ساخته شد` });
        form.reset({ nameFa: '', parentCategoryId: initialParentId });
        onCreated?.();
      },
    },
  );

  function handle(outcome: CommandOutcome<MerchantCategory>) {
    if (outcome.status !== 'error' || !isApiError(outcome.error)) return;
    setUnmapped(applyFieldErrors(outcome.error, FIELDS, form.setError));
  }

  async function submit(values: CategoryValues) {
    setUnmapped([]);
    handle(
      await command.submit({
        nameFa: values.nameFa,
        parentCategoryId: values.parentCategoryId === '' ? null : values.parentCategoryId,
      }),
    );
  }

  const errors = form.formState.errors;
  const parentLocked = initialParentId !== '';
  return (
    <form
      aria-label="ساخت دسته‌بندی"
      className="admin-form"
      noValidate
      onSubmit={form.handleSubmit(submit)}
    >
      <div className="admin-grid-2">
        <TextField
          error={errors.nameFa?.message}
          label="نام دسته‌بندی"
          {...form.register('nameFa')}
        />
        {parentLocked ? (
          <p className="admin-muted">
            {`زیرشاخهٔ «${categories.find((item) => item.id === initialParentId)?.nameFa ?? '—'}»`}
          </p>
        ) : (
          <NativeSelect
            error={errors.parentCategoryId?.message}
            label="دسته‌بندی والد"
            options={categories
              .filter((item) => item.status === 'ACTIVE')
              .map((item) => ({ label: item.pathFa, value: item.id }))}
            placeholder="بدون والد (دسته‌بندی اصلی)"
            {...form.register('parentCategoryId')}
          />
        )}
      </div>
      <FieldErrorList errors={unmapped} />
      <CommandFeedback
        onCheckStatus={() => void command.checkStatus().then(handle)}
        state={command.state}
      />
      <div className="admin-actions">
        <Button
          disabled={offline.blocked}
          loading={command.state.status === 'pending'}
          type="submit"
        >
          ساخت دسته‌بندی
        </Button>
      </div>
    </form>
  );
}

function buildCategoryTree(categories: readonly MerchantCategory[]) {
  const byParent = new Map<string | null, MerchantCategory[]>();
  for (const row of categories) {
    const key = row.parentCategoryId;
    const bucket = byParent.get(key);
    if (bucket === undefined) byParent.set(key, [row]);
    else bucket.push(row);
  }
  for (const bucket of byParent.values()) {
    bucket.sort((left, right) => left.nameFa.localeCompare(right.nameFa, 'fa'));
  }
  return byParent;
}

function CategoryTreeNode({
  canCreate,
  categories,
  childRows,
  node,
  onAddChild,
}: {
  readonly canCreate: boolean;
  readonly categories: readonly MerchantCategory[];
  readonly childRows: readonly MerchantCategory[];
  readonly node: MerchantCategory;
  readonly onAddChild: (parentId: string) => void;
}) {
  const [open, setOpen] = useState(true);
  const hasChildren = childRows.length > 0;
  return (
    <li className="admin-category-tree__item">
      <div className="admin-category-tree__row">
        <button
          aria-expanded={hasChildren ? open : undefined}
          className="admin-category-tree__toggle"
          disabled={!hasChildren}
          onClick={() => setOpen((value) => !value)}
          type="button"
        >
          {hasChildren ? <Icon name={open ? 'chevron-down' : 'chevron-right'} /> : <span />}
          <strong>{node.nameFa}</strong>
        </button>
        <span className="admin-category-tree__meta">
          <StatusChip enumName="activeStatus" value={node.status} />
          <span>{`${formatCount(node.usageCount)} فروشگاه`}</span>
        </span>
        {canCreate && node.status === 'ACTIVE' ? (
          <Button
            icon="plus"
            onClick={() => onAddChild(node.id)}
            size="sm"
            type="button"
            variant="ghost"
          >
            زیرشاخه
          </Button>
        ) : null}
      </div>
      {hasChildren && open ? (
        <ul className="admin-category-tree">
          {childRows.map((child) => (
            <CategoryTreeNode
              canCreate={canCreate}
              categories={categories}
              childRows={categories.filter((row) => row.parentCategoryId === child.id)}
              key={child.id}
              node={child}
              onAddChild={onAddChild}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

/**
 * `/merchant-categories`: the category tree, opened from «دسته‌بندی‌ها» on the merchants screen (the
 * prototype has no navigation item for it), as a wide sheet over the merchants list.
 */
export function CategoriesSheet() {
  const { self } = useOutletContext<MerchantsOutletContext>();
  const close = useCloseSheet('/merchants');
  usePageTitle('دسته‌بندی فروشگاه‌ها');
  return (
    <Dialog onOpenChange={onSheetOpenChange(close)} open size="wide" title="دسته‌بندی فروشگاه‌ها">
      <CategoriesView self={self} />
    </Dialog>
  );
}

function CategoriesView({ self }: { readonly self: AdminSelf }) {
  const categories = useCategories();
  const rows = categories.data ?? [];
  const tree = useMemo(() => buildCategoryTree(rows), [rows]);
  const roots = tree.get(null) ?? [];
  const canCreate = canPerform(self, 'createMerchantCategory');
  const [childParentId, setChildParentId] = useState<string | null>(null);

  return (
    <>
      {canCreate ? (
        <SheetSection id="category-create" title="دسته‌بندی جدید">
          <CreateCategoryForm
            categories={rows}
            initialParentId={childParentId ?? ''}
            onCreated={() => setChildParentId(null)}
          />
        </SheetSection>
      ) : null}
      <SheetSection id="category-list" title="درخت دسته‌بندی‌ها">
        <QueryState
          empty={<EmptyState title="هنوز دسته‌بندی‌ای ساخته نشده است" />}
          error={categories.error}
          isEmpty={rows.length === 0}
          onRetry={() => void categories.refetch()}
          skeleton={<Skeleton label="در حال بارگذاری دسته‌بندی‌ها…" lines={5} />}
          status={categories.status}
        >
          <ul aria-label="درخت دسته‌بندی فروشگاه‌ها" className="admin-category-tree">
            {roots.map((node) => (
              <CategoryTreeNode
                canCreate={canCreate}
                categories={rows}
                childRows={rows.filter((row) => row.parentCategoryId === node.id)}
                key={node.id}
                node={node}
                onAddChild={(parentId) => setChildParentId(parentId)}
              />
            ))}
          </ul>
        </QueryState>
      </SheetSection>
    </>
  );
}
